import { useState } from 'react';
import { Link } from 'react-router';
import { BigLink } from '@/components/BigLink';
import { ClipImage } from '@/components/ClipImage';
import { FloatingCakesHero } from '@/components/FloatingCakesHero';
import { Lightbox } from '@/components/Lightbox';
import { Meta } from '@/components/Meta';
import { ParallaxImage } from '@/components/ParallaxImage';
import { Reveal } from '@/components/Reveal';
import { Scallop } from '@/components/Scallop';
import { categorie, creazioni, pasticceria, pronti, tutteLeFoto, type Categoria } from '@/data/creazioni';
import { cn } from '@/lib/cn';


const MOBILE_LIMIT = 4; // foto mostrate su mobile prima del "Vedi tutte"

function Statement() {
  return (
    <section className="u-container py-[15vh] md:py-[20vh]">
      <div className="md:grid md:grid-cols-12">
        <div className="md:col-span-10 md:col-start-2">
          <Reveal>
            <p className="eyebrow mb-8 text-blush-deep">Come lavoro</p>
            <p className="max-w-5xl font-display text-[9vw] leading-[1.05] md:text-7xl md:leading-[1.08]">
              Faccio tutto <span className="italic text-sage-deep">a mano</span>, su ordinazione, con
              ingredienti semplici e buoni.
            </p>
            <p className="mt-8 max-w-md text-lg leading-relaxed text-ink-soft md:ml-auto md:text-right">
              Una torta, per me, è la parte più bella della festa.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Creazioni() {
  const [filtro, setFiltro] = useState<Categoria | 'tutte'>('tutte');
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const lista = filtro === 'tutte' ? creazioni : creazioni.filter((c) => c.categoria === filtro);
  const hasMoreOnMobile = lista.length > MOBILE_LIMIT;

  return (
    <section id="creazioni" className="u-container scroll-mt-24 py-[6vh]">
      <div className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="eyebrow text-blush-deep">I dolci</p>
          <h2 className="mt-4 font-display text-5xl md:text-7xl">Le mie dolcezze</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          {(['tutte', ...categorie.map((c) => c.key)] as const).map((k) => {
            const label = k === 'tutte' ? 'Tutte' : categorie.find((c) => c.key === k)?.label;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setFiltro(k);
                  setShowAll(false);
                }}
                className={cn(
                  'rounded-full border px-5 py-2 text-xs uppercase tracking-[0.18em] transition-colors',
                  filtro === k
                    ? 'border-sage-deep bg-sage-deep text-cream'
                    : 'border-line text-ink-soft hover:border-sage-deep hover:text-sage-deep',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* editorial masonry — varied heights, broken grid (not uniform cards) */}
      <div className="columns-1 gap-5 pb-[6vh] sm:columns-2 lg:columns-3 [&>*]:mb-5">
        {lista.map((c, i) => {
          const globalIndex = creazioni.findIndex((x) => x.src === c.src);
          const hideOnMobile = i >= MOBILE_LIMIT && !showAll;
          const ratios = ['aspect-4/5', 'aspect-square', 'aspect-3/4', 'aspect-5/6', 'aspect-square', 'aspect-4/5'];
          const ratio = ratios[i % ratios.length];
          return (
            <Reveal key={c.src} delay={(i % 3) * 0.07} className={cn('break-inside-avoid', hideOnMobile && 'hidden sm:block')}>
              <button
                type="button"
                onClick={() => setLightbox(globalIndex)}
                data-cursor="hover"
                aria-label={`Apri ${c.alt}`}
                className="group block w-full cursor-pointer overflow-hidden rounded-sm"
              >
                <ClipImage
                  src={c.src}
                  alt={c.alt}
                  className={cn(ratio, 'w-full')}
                  imgClassName="transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                />
              </button>
            </Reveal>
          );
        })}
      </div>

      {hasMoreOnMobile && (
        <div className="mt-8 flex justify-center sm:hidden">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="rounded-full border border-sage-deep px-7 py-3 text-xs uppercase tracking-[0.2em] text-sage-deep transition-colors hover:bg-sage-deep hover:text-cream"
          >
            {showAll ? 'Mostra meno' : `Vedi tutte (${lista.length})`}
          </button>
        </div>
      )}

      <Lightbox images={tutteLeFoto} index={lightbox} onChange={setLightbox} />
    </section>
  );
}

function Pronti() {
  return (
    <section className="relative bg-sage-deep py-[15vh] text-cream">
      <Scallop color="var(--color-cream)" />
      <div className="u-container">
        <div className="mb-12 max-w-2xl">
          <p className="eyebrow text-blush">In negozio, ogni giorno</p>
          <h2 className="mt-4 font-display text-5xl md:text-7xl">Dolci pronti</h2>
          <p className="mt-5 text-lg leading-relaxed text-cream/80">
            Non serve ordinare: passa in pasticceria e trovi dolci freschi pronti da portare via —
            muffin, bignè, crostatine e tanto altro, ogni giorno.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5 md:gap-6">
          {pronti.map((p, i) => (
            <Reveal key={p.src} delay={(i % 5) * 0.06}>
              <ClipImage src={p.src} alt={p.alt} className="aspect-square w-full rounded-sm" />
              <p className="mt-2 text-sm text-cream/70">{p.alt}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Ordina() {
  return (
    <section className="relative my-[8vh] grid items-center gap-0 md:grid-cols-2">
      <ParallaxImage src={pasticceria.ordinaImg} alt="Torta su ordinazione" className="aspect-square w-full md:aspect-auto md:h-[85vh]" />
      <div className="u-container py-16 md:py-0">
        <Reveal>
          <p className="eyebrow text-blush-deep">Su ordinazione</p>
          <h2 className="mt-5 font-display text-5xl md:text-6xl">La torta che hai in mente</h2>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-ink-soft">
            Compleanni, battesimi, lauree o anche solo una voglia di dolce: dimmi cosa ti serve e te
            la preparo. Ti rispondo io, su WhatsApp.
          </p>
          <Link
            to="/ordina"
            data-cursor="hover"
            className="mt-9 inline-flex items-center gap-3 rounded-full bg-sage-deep px-8 py-3.5 text-xs uppercase tracking-[0.2em] text-cream transition-opacity hover:opacity-90"
          >
            Ordina una torta <span aria-hidden>→</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Pasticceria() {
  return (
    <section id="pasticceria" className="u-container grid scroll-mt-24 items-center gap-12 py-[12vh] md:grid-cols-12">
      <div className="md:col-span-6">
        <ClipImage src={pasticceria.laboratorio} alt="La pasticceria a Travedona Monate" className="aspect-4/3 w-full" />
      </div>
      <div className="md:col-span-5 md:col-start-8">
        <Reveal>
          <p className="eyebrow text-blush-deep">Il laboratorio</p>
          <p className="mt-6 font-display text-3xl leading-snug md:text-4xl">
            Da {pasticceria.aperturaDal} ho aperto il mio piccolo laboratorio a {pasticceria.citta}.
          </p>
          <p className="mt-6 max-w-md leading-relaxed text-ink-soft">
            Qui preparo ogni dolce a mano, con calma e cura. Passa a trovarmi, o ordina la tua torta.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ContactCta() {
  return (
    <section className="relative bg-blush py-[17vh] text-center text-ink">
      <Scallop color="var(--color-cream)" />
      <Reveal className="u-container flex flex-col items-center">
        <p className="eyebrow mb-8 text-sage-deep">Iniziamo</p>
        <BigLink to="/ordina" className="text-[15vw] hover:opacity-70 md:text-[9vw]">
          Ordina ora
        </BigLink>
      </Reveal>
    </section>
  );
}

export function HomePage() {
  return (
    <>
      <Meta
        title="Rachele Dolcezze — Pasticceria artigianale · Travedona Monate"
        description="Torte personalizzate, cupcakes e dolci artigianali su ordinazione a Travedona Monate (VA)."
      />
      <FloatingCakesHero />
      <Statement />
      <Creazioni />
      <Pronti />
      <Ordina />
      <Pasticceria />
      <ContactCta />
    </>
  );
}
